import { Box, Button, Card, CardContent, styled, Typography } from "@mui/material";

export const StyledPageRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  paddingTop: theme.padding?.size?.medium ?? "16px",
  paddingBottom: theme.padding?.size?.large ?? "40px",
  paddingLeft: theme.padding?.size?.medium ?? "16px",
  paddingRight: theme.padding?.size?.medium ?? "16px",
  [theme.breakpoints.up("sm")]: {
    paddingLeft: theme.padding?.size?.large ?? "24px",
    paddingRight: theme.padding?.size?.large ?? "24px",
  },
}));

export const StyledLoaderWrap = styled(Box)(({ theme }) => ({
  paddingTop: theme.padding?.size?.large ?? "48px",
  paddingBottom: theme.padding?.size?.large ?? "48px",
}));

export const StyledErrorPage = styled(Box)(({ theme }) => ({
  padding: theme.padding?.size?.large ?? "24px",
  width: "100%",
}));

export const StyledBackButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.padding?.size?.medium ?? "20px",
  color: theme.palette?.custom?.primary ?? "#0B65B5",
  textTransform: "none",
}));

export const StyledErrorTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.padding?.size?.medium ?? "16px",
  marginBottom: theme.padding?.size?.small ?? "8px",
}));

export const StyledRetryButton = styled(Button)(({ theme }) => ({
  marginTop: theme.padding?.size?.medium ?? "16px",
}));

export const StyledDetailCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.borderRadius?.radius?.medium ?? "12px",
  border: `1px solid ${theme.palette?.custom?.lightPink ?? "#e8eaed"}`,
  width: "100%",
}));

export const StyledHeaderCard = styled(StyledDetailCard)(({ theme }) => ({
  marginBottom: theme.padding?.size?.medium ?? "20px",
}));

export const StyledDetailCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.padding?.size?.medium ?? "16px",
  "&:last-child": {
    paddingBottom: theme.padding?.size?.medium ?? "16px",
  },
  [theme.breakpoints.up("sm")]: {
    padding: theme.padding?.size?.large ?? "20px",
    "&:last-child": {
      paddingBottom: theme.padding?.size?.large ?? "20px",
    },
  },
}));

export const StyledHeroRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.borderRadius?.radius?.custom ?? "16px",
  [theme.breakpoints.up("sm")]: {
    gap: theme.padding?.size?.large ?? "28px",
  },
}));

export const StyledAvatarCircle = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: theme.borderRadius?.radius?.extraLarge ?? "50%",
  backgroundColor: theme.palette?.custom?.chipsBgColor ?? "#CAE6FF",
  color: theme.palette?.custom?.primary ?? "#0B65B5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: theme.typography?.fontWeight?.bold ?? 700,
  fontSize: theme.typography?.large?.fontSize ?? "1.25rem",
  flexShrink: 0,
}));

export const StyledHeroContent = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
}));

export const StyledHeroName = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography?.fontWeight?.bold ?? 700,
  color: theme.palette?.custom?.lightGrey ?? "#212121",
  fontSize: theme.typography?.large?.fontSize ?? "20px",
  lineHeight: 1.25,
}));

export const StyledContactRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "dense",
})(({ theme, dense }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.padding?.size?.small ?? "8px",
  marginTop: dense
    ? theme.padding?.size?.small ?? "6px"
    : theme.borderRadius?.radius?.custom ?? "10px",
}));

export const StyledContactIcon = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  fontSize: 17,
  color: theme.palette?.custom?.profileText ?? "#393939",
  opacity: 0.85,
}));

export const StyledContactText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography?.small?.fontSize ?? "14px",
  fontWeight: theme.typography?.fontWeight?.regular ?? 400,
  color: theme.palette?.custom?.profileText ?? "#393939",
  lineHeight: 1.5,
}));

export const StyledBadgeRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.borderRadius?.radius?.custom ?? "10px",
  marginTop: theme.padding?.size?.medium ?? "14px",
}));

export const StyledOutlineBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme, variant = "success" }) => {
  const palette = {
    success: {
      color: theme.palette?.custom?.success ?? "#00C046",
      border: theme.palette?.custom?.success ?? "#00C046",
      bg: "rgba(0, 192, 70, 0.08)",
    },
    danger: {
      color: theme.palette?.custom?.danger ?? "#FF3850",
      border: theme.palette?.custom?.danger ?? "#FF3850",
      bg: "rgba(255, 56, 80, 0.08)",
    },
    warning: {
      color: theme.palette?.custom?.warning ?? "#FF8500",
      border: theme.palette?.custom?.warning ?? "#FF8500",
      bg: "rgba(255, 133, 0, 0.08)",
    },
  };
  const colors = palette[variant] ?? palette.warning;

  return {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.padding?.size?.small ?? "6px",
    padding: `${theme.padding?.size?.small ?? "4px"} ${theme.borderRadius?.radius?.custom ?? "10px"}`,
    borderRadius: theme.borderRadius?.radius?.large ?? "20px",
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.bg,
    color: colors.color,
    fontSize: theme.typography?.small?.fontSize ?? "0.8125rem",
    fontWeight: theme.typography?.fontWeight?.semiBold ?? 600,
  };
});

export const StyledBadgeDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme, variant = "success" }) => {
  const dotColor = {
    success: theme.palette?.custom?.success ?? "#00C046",
    danger: theme.palette?.custom?.danger ?? "#FF3850",
    warning: theme.palette?.custom?.warning ?? "#FF8500",
  };
  return {
    width: 7,
    height: 7,
    borderRadius: theme.borderRadius?.radius?.extraLarge ?? "50%",
    backgroundColor: dotColor[variant] ?? dotColor.warning,
    flexShrink: 0,
  };
});

export const StyledShopperIdText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography?.small?.fontSize ?? "0.8125rem",
  fontWeight: theme.typography?.fontWeight?.regular ?? 400,
  color: theme.palette?.custom?.profileText ?? "#393939",
  lineHeight: 1.5,
  marginTop: theme.padding?.size?.medium ?? "12px",
  fontFamily: "monospace",
  wordBreak: "break-all",
}));

export const StyledMemberSinceHighlight = styled(Box)(({ theme }) => ({
  fontWeight: theme.typography?.fontWeight?.semiBold ?? 600,
  color: theme.palette?.custom?.lightGrey ?? "#212121",
}));

export const StyledSectionHeadingRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.borderRadius?.radius?.custom ?? "10px",
  marginBottom: theme.padding?.size?.medium ?? "16px",
}));

export const StyledSectionIconBox = styled(Box)(({ theme }) => ({
  width: 34,
  height: 34,
  borderRadius: theme.borderRadius?.radius?.wallet ?? "8px",
  backgroundColor: theme.palette?.custom?.chipsBgColor ?? "#CAE6FF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: theme.palette?.custom?.primary ?? "#0B65B5",
  fontSize: 18,
}));

export const StyledSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography?.fontWeight?.bold ?? 700,
  fontSize: theme.typography?.regular?.fontSize ?? "16px",
  color: theme.palette?.custom?.lightGrey ?? "#212121",
}));

export const StyledProfileFieldRoot = styled(Box)(() => ({}));

export const StyledProfileFieldLabelRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.padding?.size?.small ?? "6px",
  marginBottom: theme.padding?.size?.small ?? "4px",
}));

export const StyledFieldIcon = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  fontSize: 14,
  color: theme.palette?.custom?.primary ?? "#0B65B5",
  opacity: 0.85,
}));

export const StyledFieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography?.mini?.fontSize ?? "12px",
  fontWeight: theme.typography?.fontWeight?.medium ?? 500,
  color: theme.palette?.custom?.profileText ?? "#393939",
  lineHeight: 1.3,
}));

export const StyledFieldValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "monospace" && prop !== "indented",
})(({ theme, monospace, indented }) => ({
  fontSize: theme.typography?.small?.fontSize ?? "14px",
  fontWeight: theme.typography?.fontWeight?.medium ?? 500,
  color: theme.palette?.custom?.lightGrey ?? "#212121",
  lineHeight: 1.4,
  marginTop: 0,
  paddingLeft: indented ? "22px" : 0,
  wordBreak: monospace ? "break-all" : "normal",
}));

export const StyledStatTilesRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.padding?.size?.medium ?? "12px",
}));

export const StyledStatTile = styled(Box, {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme, variant = "default" }) => {
  const palette = {
    default: {
      bg: theme.palette?.custom?.screenBgColor ?? "#f4f7fb",
      color: theme.palette?.custom?.primary ?? "#0B65B5",
    },
    success: {
      bg: "rgba(0, 192, 70, 0.1)",
      color: theme.palette?.custom?.success ?? "#00C046",
    },
    warning: {
      bg: "rgba(255, 133, 0, 0.1)",
      color: theme.palette?.custom?.warning ?? "#FF8500",
    },
  };
  const colors = palette[variant] ?? palette.default;

  return {
    flex: "1 1 120px",
    minWidth: 120,
    padding: theme.padding?.size?.medium ?? "14px",
    borderRadius: theme.borderRadius?.radius?.medium ?? "12px",
    backgroundColor: colors.bg,
    border: `1px solid ${theme.palette?.custom?.lightPink ?? "#e8eaed"}`,
    color: colors.color,
  };
});

export const StyledStatTileLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography?.small?.fontSize ?? "0.75rem",
  fontWeight: theme.typography?.fontWeight?.semiBold ?? 600,
  color: theme.palette?.custom?.profileText ?? "#393939",
}));

export const StyledStatTileValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme, variant = "default" }) => {
  const colorMap = {
    default: theme.palette?.custom?.primary ?? "#0B65B5",
    success: theme.palette?.custom?.success ?? "#00C046",
    warning: theme.palette?.custom?.warning ?? "#FF8500",
  };

  return {
    fontSize: theme.typography?.large?.fontSize ?? "20px",
    fontWeight: theme.typography?.fontWeight?.bold ?? 700,
    color: colorMap[variant] ?? colorMap.default,
    marginTop: theme.padding?.size?.small ?? "4px",
  };
});

export const StyledTableScroll = styled(Box)(() => ({
  overflowX: "auto",
}));

export const StyledEmptyStateText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography?.base?.fontSize ?? "0.875rem",
  fontWeight: theme.typography?.fontWeight?.regular ?? 400,
  color: theme.palette?.custom?.profileText ?? "#393939",
  lineHeight: 1.5,
}));
